<!-- routes/ComingSoon/Comparison.svelte -->
<script>
  import Container from "lib/components/Container.svelte";
  import { _ } from "svelte-i18n";

  // Hold duration data with gold prices
  const holdDurationData = {
    "1 week": { price: 5201.64, date: "20/08/2025" },
    "2 weeks": { price: 5166.59, date: "13/08/2025" },
    "1 month": { price: 5121.67, date: "30/07/2025" },
    "2 months": { price: 5013.85, date: "26/06/2025" },
    "3 months": { price: 5114.52, date: "29/05/2025" },
    "4 months": { price: 5161.08, date: "28/04/2025" },
    "5 months": { price: 4932.72, date: "31/03/2025" },
    "6 months": { price: 4546.92, date: "28/02/2025" },
    "7 months": { price: 4493.64, date: "31/01/2025" },
    "8 months": { price: 4190.53, date: "31/12/2024" },
    "9 months": { price: 4068.29, date: "30/11/2024" },
    "10 months": { price: 4236.95, date: "31/10/2024" },
    "11 months": { price: 3832.85, date: "30/09/2024" },
    "12 months": { price: 3696.05, date: "31/08/2024" },
  };

  const holdDurationOptions = [
    "1 week",
    "2 weeks",
    "1 month",
    "2 months",
    "3 months",
    "4 months",
    "5 months",
    "6 months",
    "7 months",
    "8 months",
    "9 months",
    "10 months",
    "11 months",
    "12 months",
  ];

  // Constants
  const INITIAL_INVESTMENT_AUD = 2000;
  const TODAY_GOLD_PRICE_AUD = 5219.39;
  const AUD_TO_CNY_RATE = 4.64652;

  // Liquid Noble fee structure
  const GOLD_SPREAD = 0.01; // 1% spread
  const BUY_ORDER_FEE = 0.0022; // 0.22% fee for buy orders
  const SELL_ORDER_FEE = 0.0033; // 0.33% fee for sell orders
  const TRADING_FEE_AUD = 19.25; // Fixed trading fee

  // Combined rates
  const BUY_REDUCTION_RATE = GOLD_SPREAD + BUY_ORDER_FEE; // 1.22%
  const SELL_REDUCTION_RATE = GOLD_SPREAD + SELL_ORDER_FEE; // 1.33%

  const INSTAREM_CNY = 9253.91; // Best alternative for comparison

  // Reactive state
  let selectedDuration = "12 months";

  // Calculate dynamic values for all providers
  $: {
    const startData = holdDurationData[selectedDuration];
    const startPriceAUD = startData.price;

    // Calculate net investment after trading fee
    const netInvestmentAUD = INITIAL_INVESTMENT_AUD - TRADING_FEE_AUD;

    // Calculate effective amount for gold purchase (after spread and buy fee)
    const effectiveInvestmentAUD = netInvestmentAUD * (1 - BUY_REDUCTION_RATE);

    // Calculate grams purchased initially
    const gramsInitiallyPurchased = effectiveInvestmentAUD / startPriceAUD;

    // Calculate value when selling (before spread and sell fee)
    const grossValueInAUDToday = gramsInitiallyPurchased * TODAY_GOLD_PRICE_AUD;

    // Calculate net value after sell reduction (spread + sell fee)
    const netValueInAUDToday = grossValueInAUDToday * (1 - SELL_REDUCTION_RATE);

    // Convert to CNY
    const liquidNobleValueInCNY = netValueInAUDToday * AUD_TO_CNY_RATE;

    // Update base data for Liquid Noble
    baseProviderData[0].recipientGetsCNY = liquidNobleValueInCNY;
    baseProviderData[0].note = `Gold appreciation ${startData.date}→27/08/2025`;
    baseProviderData[0].exchangeRate = AUD_TO_CNY_RATE.toFixed(5); // Show AUD->CNY as main rate
    baseProviderData[0].goldRate = startPriceAUD.toFixed(2);

    // Everyone compares against Liquid Noble (but Liquid Noble shows its advantage over best traditional provider)
    const liquidNobleCNY = baseProviderData[0].recipientGetsCNY; // Liquid Noble is at index 0
    const instaremCNY = baseProviderData[1].recipientGetsCNY; // Instarem (best traditional) is at index 1

    // Build comparison data with dynamic gain calculations
    comparisonData = baseProviderData.map((provider, index) => {
      const providerCNY = provider.recipientGetsCNY;

      let gainAmount, gainPercentage;

      if (index === 0) {
        // Liquid Noble shows advantage over Instarem
        gainAmount = liquidNobleCNY - instaremCNY;
        gainPercentage = ((gainAmount / instaremCNY) * 100).toFixed(1);
      } else {
        // Other providers show their deficit vs Liquid Noble
        gainAmount = providerCNY - liquidNobleCNY;
        gainPercentage = ((gainAmount / liquidNobleCNY) * 100).toFixed(1);
      }

      return {
        ...provider,
        recipientGets: `~${Math.round(providerCNY).toLocaleString()} CNY`,
        gainVsBest:
          gainAmount > 0
            ? `+${Math.round(Math.abs(gainAmount)).toLocaleString()} CNY`
            : `-${Math.round(Math.abs(gainAmount)).toLocaleString()} CNY`,
        gainPercentage:
          gainAmount > 0
            ? `(+${Math.abs(gainPercentage)}%)²`
            : `(-${Math.abs(gainPercentage)}%)²`,
      };
    });
  }

  // Static base data for providers
  const baseProviderData = [
    {
      provider: "",
      logo: "v2/lqn_logo_full.png",
      recipientGetsCNY: 0, // Will be calculated dynamically
      exchangeRateMarkup: "0 AUD",
      exchangeRate: "", // Will be set dynamically to show AUD->CNY rate
      transferFee: "19.25 AUD",
      totalCost: "19.25 AUD",
      highlight: true,
      note: "", // Will be set dynamically
    },
    {
      provider: "",
      logo: "instarem_logo.svg",
      recipientGetsCNY: 9253.91,
      exchangeRateMarkup: "1.02 AUD",
      exchangeRate: "4.64414",
      transferFee: "7.40 AUD",
      totalCost: "8.42 AUD",
      highlight: false,
    },
    {
      provider: "",
      logo: "remitly_logo.svg",
      recipientGetsCNY: 9053.47,
      exchangeRateMarkup: "49.62 AUD",
      exchangeRate: "4.53124",
      transferFee: "1.99 AUD",
      totalCost: "51.61 AUD",
      highlight: false,
    },
    {
      provider: "",
      logo: "nab_logo.svg",
      recipientGetsCNY: 9015.36,
      exchangeRateMarkup: "59.76 AUD",
      exchangeRate: "4.50768",
      transferFee: "0 AUD",
      totalCost: "59.76 AUD",
      highlight: false,
    },
  ];

  // Reactive comparison data
  let comparisonData = [];
</script>

<!-- Comparison Table -->
<Container variant="light" class="comparison-section">
  <div class="comparison-header">
    <h2 class="section-title">{$_("comparison.title")}</h2>
    <p class="section-subtitle">
      {$_("comparison.subtitle")}
    </p>

    <!-- Hold Duration Slider -->
    <div class="duration-slider-container">
      <label class="slider-label">{$_("comparison.holdDuration")}</label>
      <div class="slider-wrapper">
        <input
          type="range"
          min="0"
          max={holdDurationOptions.length - 1}
          value={holdDurationOptions.indexOf(selectedDuration)}
          class="duration-slider"
          on:input={(e) =>
            (selectedDuration = holdDurationOptions[e.target.value])}
        />
        <div class="slider-labels">
          {#each holdDurationOptions as option, index}
            {@const parts = option.split(" ")}
            {@const number = parts[0]}
            {@const unit = parts[1]}
            <span
              class="slider-tick"
              class:active={option === selectedDuration}
            >
              {number}{$_(
                unit === "week"
                  ? "comparison.duration.week"
                  : unit === "weeks"
                    ? "comparison.duration.weeks"
                    : unit === "month"
                      ? "comparison.duration.month"
                      : "comparison.duration.months"
              )}
            </span>
          {/each}
        </div>
      </div>
      <div class="selected-duration">
        <strong>{selectedDuration}</strong>
        <span class="duration-date"
          >({holdDurationData[selectedDuration].date})</span
        >
      </div>
    </div>
  </div>

  <div class="comparison-table-container">
    <div class="table-header">
      <div class="header-cell amount-header">
        <div class="amount-input">
          <span class="amount-value">2,000</span>
        </div>
      </div>
      <div class="header-cell from-header">
        <div class="currency-selector">
          <img
            src="/legal/images/Australia.svg"
            alt="Australia"
            class="flag-icon"
          />
          <span>{$_("comparison.currency.aud")}</span>
        </div>
      </div>
      <div class="arrow-container">
        <div class="conversion-arrow">→</div>
      </div>
      <div class="header-cell to-header">
        <div class="currency-selector">
          <img src="/legal/images/China.svg" alt="China" class="flag-icon" />
          <span>{$_("comparison.currency.cny")}</span>
        </div>
      </div>
    </div>

    <div class="comparison-table">
      {#each comparisonData as provider}
        <div class="provider-row" class:highlighted={provider.highlight}>
          <div class="provider-info">
            <div class="provider-logo">
              <img
                src={`/images/${provider.logo}`}
                alt={provider.provider}
                class="provider-logo-image"
              />
            </div>
            {#if provider.provider}
              <div class="provider-name">
                {provider.provider}
                {#if provider.note}
                  <div class="provider-note">{provider.note}</div>
                {/if}
              </div>
            {/if}
          </div>

          <div class="provider-metrics">
            <div class="metric">
              <div class="metric-label">{$_("comparison.recipientGets")}</div>
              <div class="metric-value main-value">
                {provider.recipientGets}
              </div>
            </div>

            <div class="metric">
              <div class="metric-label">
                {$_("comparison.exchangeRateMarkup")}
              </div>
              <div class="metric-value">{provider.exchangeRateMarkup}</div>
            </div>

            <div class="metric">
              <div class="metric-label">{$_("comparison.exchangeRate")}</div>
              <div class="metric-value">{provider.exchangeRate}</div>
              <div class="metric-sublabel">
                {#if provider.highlight}
                  <div class="exchange-rate-line">
                    {$_("comparison.exchangeRateNote")}
                  </div>
                  <div class="gold-rate-line">{provider.goldRate}</div>
                  <div class="exchange-rate-line">
                    {$_("comparison.goldRateNote")}
                  </div>
                {:else}
                  {$_("comparison.exchangeRateNote")}
                {/if}
              </div>
            </div>

            <div class="metric">
              <div class="metric-label">{$_("comparison.transferFee")}</div>
              <div class="metric-value">{provider.transferFee}</div>
            </div>

            <div class="metric">
              <div class="metric-label">{$_("comparison.totalCost")}</div>
              <div class="metric-value exchange-cost">{provider.totalCost}</div>
            </div>

            <div class="metric net-gain-metric">
              <div class="metric-label">{$_("comparison.netGain")}</div>
              <div
                class="metric-value gain-value"
                class:positive-gain={provider.gainVsBest &&
                  provider.gainVsBest.startsWith("+")}
                class:negative-gain={provider.gainVsBest &&
                  provider.gainVsBest.startsWith("-")}
              >
                {provider.gainVsBest || $_("comparison.baseline")}
              </div>
              {#if provider.gainPercentage}
                <div
                  class="gain-percentage"
                  class:positive-gain={provider.gainVsBest &&
                    provider.gainVsBest.startsWith("+")}
                  class:negative-gain={provider.gainVsBest &&
                    provider.gainVsBest.startsWith("-")}
                >
                  {provider.gainPercentage}
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>
  </div>

  <div class="comparison-disclaimer">
    {@html $_("comparison.disclaimer")}
  </div>
</Container>

<style lang="scss">
  /* Duration Slider Styles */
  .duration-slider-container {
    max-width: 600px;
    margin: 2rem auto;
    padding: 1.5rem;
    background: rgba(210, 165, 82, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(210, 165, 82, 0.2);
  }

  .slider-label {
    display: block;
    font-weight: 600;
    color: #2c1810;
    margin-bottom: 1rem;
    text-align: center;
  }

  .slider-wrapper {
    position: relative;
    margin-bottom: 1rem;
  }

  .duration-slider {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(to right, #d2a552, #f6efd3);
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #d2a552;
      cursor: pointer;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }

    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #d2a552;
      cursor: pointer;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }
  }

  .slider-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 0.5rem;
    font-size: 0.75rem;
  }

  .slider-tick {
    color: #6c757d;
    font-size: 0.7rem;
    transition: all 0.2s ease;

    &.active {
      color: #d2a552;
      font-weight: 600;
    }
  }

  .selected-duration {
    text-align: center;
    font-size: 1.1rem;
    color: #2c1810;
  }

  .duration-date {
    color: #6c757d;
    font-size: 0.9rem;
    margin-left: 0.5rem;
  }

  /* Comparison Section */
  .comparison-section {
    padding: 4rem 0;
  }

  .comparison-header {
    text-align: center;
    max-width: 800px;
    margin: 0 auto 3rem;
  }

  .section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c1810;
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  .section-subtitle {
    font-size: 1.1rem;
    line-height: 1.6;
    color: #5d4a3a;
  }

  .comparison-table-container {
    max-width: 1000px;
    margin: 0 auto;
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .comparison-disclaimer {
    font-size: 0.85rem;
    color: #6c757d;
    text-align: left;
    margin-top: 1rem;
    font-style: italic;
  }

  .table-header {
    display: grid;
    grid-template-columns: 1fr 2fr auto 2fr;
    gap: 1rem;
    padding: 1.5rem;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    align-items: center;
  }

  .header-cell {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .amount-input {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 0.75rem;
    font-weight: 600;
    color: #6c757d;
    cursor: default;
  }

  .amount-value {
    font-size: 1.1rem;
  }

  .currency-selector {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 0.75rem;
    color: #6c757d;
    cursor: default;
  }

  .arrow-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 0.5rem;
  }

  .conversion-arrow {
    font-size: 24px;
    color: #6c757d;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .flag-icon {
    width: 20px;
    height: 15px;
    border-radius: 2px;
  }

  .comparison-table {
    display: flex;
    flex-direction: column;
  }

  .provider-row {
    display: grid;
    grid-template-columns: 200px 1fr;
    border-bottom: 1px solid #e9ecef;
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
    }

    &.highlighted {
      background: linear-gradient(
        135deg,
        rgba(210, 165, 82, 0.1),
        rgba(246, 239, 211, 0.1)
      );
      border: 2px solid rgba(210, 165, 82, 0.3);
    }
  }

  .provider-info {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 1.5rem;
    border-right: 1px solid #e9ecef;
  }

  .provider-logo {
    font-size: 1.5rem;
    border-radius: 0;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .provider-logo-image {
    width: 128px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.5));
  }

  .provider-name {
    font-weight: 600;
    color: #495057;
  }

  .provider-note {
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: 400;
    margin-top: 0.25rem;
  }

  .provider-metrics {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    align-items: center;
    min-height: 120px; /* Ensure consistent row height */
  }

  .metric {
    padding: 1.5rem 0.8rem; /* Reduced horizontal padding */
    text-align: center;
    border-right: 1px solid #e9ecef;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100px; /* Consistent metric height */

    &:last-child {
      border-right: none;
    }
  }

  .metric-label {
    font-size: 0.75rem; /* Slightly smaller for better fit */
    color: #6c757d;
    margin-bottom: 0.5rem;
    font-weight: 500;
    line-height: 1.2; /* Tighter line height */
    min-height: 2.4rem; /* Reserve space for 2 lines */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .metric-value {
    font-weight: 600;
    color: #495057;
    font-size: 0.85rem; /* Slightly smaller */
    line-height: 1.3;
  }

  .metric-sublabel {
    font-size: 0.7rem; /* Smaller text */
    color: #6c757d;
    margin-top: 0.25rem;
    line-height: 1.2; /* Tighter spacing */
    min-height: 2.8rem; /* Reserve space for multi-line content */
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .exchange-rate-line,
  .gold-rate-line {
    margin-bottom: 0.15rem; /* Tighter spacing */
    white-space: nowrap; /* Prevent wrapping */

    &:last-child {
      margin-bottom: 0;
    }
  }

  .gold-rate-line {
    font-weight: 600;
    color: #495057;
    font-size: 0.8rem; /* Smaller gold rate */
    margin: 0.4rem 0 0.15rem 0;
  }

  .main-value {
    font-size: 1rem;
    color: #28a745;
  }

  .exchange-cost {
    font-size: 1rem;
  }

  .net-gain-metric {
    background: rgba(0, 0, 0, 0.02);
    font-weight: 600;
  }

  .gain-value {
    font-size: 1.1rem;
    font-weight: 700;

    &.positive-gain {
      color: #28a745;
    }

    &.negative-gain {
      color: #dc3545;
    }
  }

  .gain-percentage {
    font-size: 0.8rem;
    font-weight: 600;
    margin-top: 0.2rem;

    &.positive-gain {
      color: #28a745;
    }

    &.negative-gain {
      color: #dc3545;
    }
  }

  /* Tablet Responsive Design */
  @media (max-width: 1024px) and (min-width: 769px) {
    .section-title {
      font-size: 2.2rem;
    }

    .comparison-table-container {
      max-width: 900px;
    }

    .provider-metrics {
      grid-template-columns: repeat(3, 1fr);
    }

    .metric {
      padding: 1.2rem 0.8rem;
    }

    .net-gain-metric {
      grid-column: span 3;
      border-top: 3px solid #28a745;
      background: rgba(40, 167, 69, 0.05);
      margin-top: 0.5rem;
    }
  }

  /* Mobile Responsive Design */
  @media (max-width: 768px) {
    .section-title {
      font-size: 2rem;
    }

    .duration-slider-container {
      margin: 1.5rem 0;
      padding: 1rem;
    }

    .slider-labels {
      flex-wrap: wrap;
      gap: 0.25rem;
      justify-content: center; /* Better centering for wrapped labels */
    }

    .slider-tick {
      font-size: 0.6rem;
      padding: 0.1rem;
    }

    .table-header {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .arrow-container {
      display: none;
    }

    .provider-row {
      grid-template-columns: 1fr;
    }

    .provider-info {
      border-right: none;
      border-bottom: 1px solid #e9ecef;
      padding: 1rem;
    }

    .provider-metrics {
      grid-template-columns: 1fr;
      text-align: left;
      min-height: auto; /* Remove fixed height on mobile */
    }

    .metric {
      border-right: none;
      border-bottom: 1px solid #e9ecef;
      text-align: left;
      padding: 1rem;
      min-height: auto; /* Remove fixed height on mobile */
      display: block; /* Change from flex to block for better mobile layout */

      &:last-child {
        border-bottom: none;
      }
    }

    .metric-label {
      min-height: auto; /* Remove reserved space on mobile */
      margin-bottom: 0.3rem;
      font-size: 0.8rem;
      text-align: left;
      display: block;
    }

    .metric-sublabel {
      min-height: auto; /* Remove reserved space on mobile */
      margin-bottom: 0.5rem;
      font-size: 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .exchange-rate-line,
    .gold-rate-line {
      margin-bottom: 0.2rem;
      white-space: normal; /* Allow wrapping on mobile */

      &:last-child {
        margin-bottom: 0;
      }
    }

    .gold-rate-line {
      font-weight: 600;
      color: #495057;
      font-size: 0.8rem;
      margin: 0.3rem 0;
    }

    .net-gain-metric {
      border-left: none;
      border-top: 3px solid #28a745;
      background: rgba(40, 167, 69, 0.05);
      margin-top: 0.5rem;
      padding: 1.2rem 1rem;
    }
  }

  @media (max-width: 480px) {
    .section-title {
      font-size: 1.75rem;
    }

    .section-subtitle {
      font-size: 1rem;
    }

    .comparison-table-container {
      margin: 0 0;
    }

    .table-header {
      padding: 1rem;
    }

    .provider-info {
      padding: 1rem;
    }

    .metric-sublabel {
      font-size: 0.8rem;
    }

    .exchange-rate-line {
      font-size: 0.8rem;
    }

    .gold-rate-line {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 360px) {
    .section-title {
      font-size: 1.5rem;
    }

    .comparison-table-container {
      margin: 0 0;
    }
  }

  @media (max-width: 400px) {
    /* Fix comparison table container */
    .comparison-table-container {
      width: 100%;
      max-width: 100%;
      overflow-x: auto;
    }

    .comparison-header {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }

    .comparison-table-container {
      margin: 0 0;
    }
  }
</style>
